import { useCallback, useEffect, useMemo } from 'react';

import { DialogsContainer } from 'features/store/components/auto-select-closest-store-prompt/dialogs-container';
import { useAuoSelectClosestStorePrompt } from 'features/store/components/auto-select-closest-store-prompt/hooks/use-auto-select-closest-store-prompt';
import { useFocusRef } from 'features/store/components/auto-select-closest-store-prompt/hooks/use-focus-ref';
import { NearbyStoreDetails } from 'features/store/components/auto-select-closest-store-prompt/nearby-store-details';
import {
  ActionButtonOutline,
  Container,
  DisclaimerText,
  HeaderText,
  StyledActionButton,
} from 'features/store/components/auto-select-closest-store-prompt/styles';
import { NonTraditionalStoreDialogDrawer } from 'features/store/pages/pickup-store-selector/components/non-traditional-store-dialog-drawer';
import { useNonTraditionalStoreUI } from 'features/store/pages/pickup-store-selector/hooks/use-non-traditional-store';
import { StyleSheet, View } from 'react-native';

import { ILocation } from '@rbi-ctg/frontend';
import { A11yWrapper } from 'components/a11y-wrapper';
import { ActionButtonVariants } from 'components/action-button/types';
import { BottomSheet } from 'components/bottom-sheet';
import { StoreLocationMap } from 'components/store-location-map';
import { ButtonClickSource, CustomEventNames } from 'state/amplitude';
import { RunningExperiments, useExperiments } from 'state/experiments';
import { t } from 'state/intl/translations';

/**
 *
 * Display a prompt to the user when a store closer to their current location is found  **but only if the user already has a store selected**.
 * It presents the details of the nearby store  (address, service mode, and distance) and allows the user to choose whether to switch to that store
 * or keep their current selection.
 *
 */
export const AutoSelectClosestStorePrompt = ({
  userCoordinates,
  activeStoreId,
}: {
  activeStoreId: string;
  userCoordinates: ILocation;
}) => {
  const { logExposure } = useExperiments();

  const headerText = t('weFoundStoreCloserToYouV2');
  const {
    nearbyStoreData,
    distanceText,
    confirmRestaurant,
    isOpen,
    handleCloseAutoSelectPrompt,
    isLoading,
  } = useAuoSelectClosestStorePrompt({ userCoordinates, activeStoreId });

  const { focusRef } = useFocusRef({ isFocus: isOpen, announce: headerText });
  const nonTraditionalStoreUI = useNonTraditionalStoreUI({
    restaurant: nearbyStoreData?.restaurant,
  });

  const handleOnSelectStore = useCallback(() => {
    nonTraditionalStoreUI?.isNonTraditionalStore
      ? nonTraditionalStoreUI.openDrawer()
      : confirmRestaurant();
  }, [confirmRestaurant, nonTraditionalStoreUI]);

  const handleOnConfirmNonTraditionalStoreDialog = useCallback(() => {
    confirmRestaurant();
    nonTraditionalStoreUI?.closeDrawer();
  }, [confirmRestaurant, nonTraditionalStoreUI]);

  useEffect(() => {
    if (nearbyStoreData?.restaurant?._id) {
      logExposure(RunningExperiments.LocationModalWithMap);
    }
  }, [nearbyStoreData?.restaurant?._id, logExposure]);

  // memo to ensure stable reference for StoreLocationMap prop
  const storeLocation = useMemo(
    () => ({
      lat: nearbyStoreData?.restaurant?.latitude ?? 0,
      lng: nearbyStoreData?.restaurant?.longitude ?? 0,
    }),
    [nearbyStoreData?.restaurant?.latitude, nearbyStoreData?.restaurant?.longitude]
  );

  if (!nearbyStoreData?.restaurant?._id) {
    return null;
  }

  if (nonTraditionalStoreUI?.isDialogDrawerOpen) {
    return (
      <NonTraditionalStoreDialogDrawer
        onConfirm={handleOnConfirmNonTraditionalStoreDialog}
        onCancel={nonTraditionalStoreUI.closeDrawer}
        isOpen={nonTraditionalStoreUI.isDialogDrawerOpen}
        accessInstructions={nonTraditionalStoreUI.accessInstructions}
      />
    );
  }
  return (
    <BottomSheet
      onClose={handleCloseAutoSelectPrompt}
      isOpen={isOpen}
      onOpenEventMessage="Open Auto Select Closest Store Prompt"
      onCloseEventMessage="Close Auto Select Closest Store Prompt"
    >
      <View ref={focusRef} style={styles.contentContainer}>
        <Container style={styles.container}>
          <A11yWrapper role="heading">
            <HeaderText>{headerText}</HeaderText>
          </A11yWrapper>
          {storeLocation.lat && storeLocation.lng && (
            <View style={styles.mapContainer}>
              <StoreLocationMap storeLocation={storeLocation} showUserMarker />
            </View>
          )}
          <NearbyStoreDetails
            address={nearbyStoreData?.restaurant.physicalAddress?.address1 ?? ''}
            distance={distanceText}
          />
          <A11yWrapper>
            <DisclaimerText>{t('changeStoreMessagePromptDisclaimerV2')}</DisclaimerText>
          </A11yWrapper>
          <StyledActionButton
            disabled={isLoading}
            isLoading={isLoading}
            onPress={handleOnSelectStore}
            fullWidth
            eventName={CustomEventNames.BUTTON_CLICK}
            eventAttributes={{
              Name: CustomEventNames.BUTTON_CLICK_ORDER_HERE,
              Source: ButtonClickSource.AUTO_SELECT_CLOSER_RESTAURANT_MODAL,
            }}
          >
            {t('orderHere')}
          </StyledActionButton>
          <ActionButtonOutline
            eventName={CustomEventNames.BUTTON_CLICK}
            eventAttributes={{
              Name: CustomEventNames.BUTTON_CLICK_KEEP_CURRENT_LOCATION,
              Source: ButtonClickSource.AUTO_SELECT_CLOSER_RESTAURANT_MODAL,
            }}
            onPress={handleCloseAutoSelectPrompt}
            fullWidth
            disabled={isLoading}
            variant={ActionButtonVariants.TEXT_ONLY}
          >
            {t('keepCurrentLocation')}
          </ActionButtonOutline>
        </Container>
        <DialogsContainer />
      </View>
    </BottomSheet>
  );
};

const styles = StyleSheet.create({
  contentContainer: {
    width: '100%',
    alignItems: 'center',
  },
  container: {
    paddingHorizontal: 24,
  },
  mapContainer: {
    width: '100%',
    borderRadius: 10,
    overflow: 'hidden',
    height: 172,
    marginTop: 12,
  },
});
